import { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import { metrics } from '.';
import type { ComponentType, FC } from 'react';

// Upgrading to emotion@11.13.3 breaks prop spread into HOC pattern
// https://github.com/emotion-js/emotion/issues/3245
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HOCProps = any;

type OnComplete = () => void;

type ChildTracker = (componentName: string) => OnComplete;

interface LoadedState {
  trackChild: ChildTracker;
  rendered: OnComplete;
}

interface WithMeasureOptions {
  /**
   * set to true if this withMeasure is to be used as only dependent to a parent
   * withMeasure component and not be tracked itself
   */
  dependentOnly?: boolean;
}

const setupLoadedState = (
  componentName: string,
  parent?: LoadedState,
  options?: WithMeasureOptions
): LoadedState => {
  let finishMeasure: OnComplete | undefined;
  if (options?.dependentOnly !== true && componentName) {
    finishMeasure = metrics.logDurationMetric('component.load', {
      context: { componentName },
    });
  }

  const notifyParent = parent?.trackChild(componentName);
  let loadingCount = 0;
  let rendered = false;
  let loaded = false;

  const checkDone = () => {
    if (!rendered || loadingCount > 0) return;
    if (loaded) return;
    loaded = true;
    notifyParent?.();
    if (options?.dependentOnly === true) return;
    finishMeasure?.();
  };

  return {
    trackChild: () => {
      loadingCount += 1;
      return () => {
        loadingCount -= 1;
        checkDone();
      };
    },
    rendered: () => {
      rendered = true;
      checkDone();
    },
  };
};

const context = createContext<LoadedState | undefined>(undefined);

/**
 * An HoC to track the load time of components in a generic fashion.
 * This HoC will log a `component.load` duration metric after the first DOM update from the component
 * using useLayoutEffect. It will also wait for all child components that have been decorated
 * with `withMeasure` to finish before considering itself loaded
 */
const withMeasure = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: WithMeasureOptions
): ComponentType<P> => {
  let componentName = WrappedComponent.name;
  if (WrappedComponent.displayName) componentName = WrappedComponent.displayName;

  const WithMeasure: FC<P> = (props: P) => {
    const parentRegistrar = useContext(context);
    const thisRegistrar = useMemo(
      () => setupLoadedState(componentName, parentRegistrar, options),
      []
    );

    useLayoutEffect(() => {
      thisRegistrar.rendered();
    }, []);

    return (
      <context.Provider value={thisRegistrar}>
        <WrappedComponent {...(props as HOCProps)} />
      </context.Provider>
    );
  };

  WithMeasure.displayName = `withMeasure(${componentName})`;
  return WithMeasure as unknown as ComponentType<P>;
};

/**
 * Convenience HoC to only affect the metric load time of the parent withMeasure,
 * not logging a metric for itself
 */
const withMeasureDependent = <P extends object>(
  WrappedComponent: ComponentType<P>
): ComponentType<P> => withMeasure(WrappedComponent, { dependentOnly: true });

export type { LoadedState, WithMeasureOptions };
export { withMeasureDependent, setupLoadedState };
export default withMeasure;
